import React from 'react';
import { forwardRef, useImperativeHandle } from 'react';
import { message } from 'antd';
import { Form } from '@ant-design/compatible';
import { FormComponentProps } from '@ant-design/compatible/lib/form';
import { Element } from 'react-scroll';
import Button from '../../../../../../components/Button';
import Tabs from '../../../../../../components/Tabs/Tabs';
import { asyncSimpleModal } from '../../../../../../lib/common';
import { SectionContent, SectionTab } from '../../../types';
import {
  EVENT_SECTION_TYPE_V2,
  MAXIMUM_NUMBER_OF_EVENT_SECTION,
  MINIMUM_NUMBER_OF_EVENT_SECTION,
} from '../../../../../../constants/service/app/eventConstants';

interface ITabListProps {
  value?: SectionTab[];
  contentList: SectionContent[];
  onChange?: (value: SectionTab[]) => void;
  eventSectionTabKey: number | null;
  setEventSectionTabKey: React.Dispatch<React.SetStateAction<number | null>>;
}
const TabList = forwardRef((props: ITabListProps, ref) => {
  const { value = [], contentList, onChange, eventSectionTabKey, setEventSectionTabKey } = props;

  useImperativeHandle(ref, () => ({
    onAddSection: addSection,
    onDeleteSection: deleteSection,
    onChangeSection: changeSection,
    onChangeTab: handleChangeTab,
  }));

  const addSection = () => {
    if (!onChange) return;
    const maxIndex = Math.max(...value.map((item) => Number(item.name.split('-')[2])));

    const changeValue = [
      ...value,
      { id: maxIndex + 1, name: `section-item-${maxIndex + 1}`, type: 'image', isSectionMobility: false },
    ] as SectionTab[];

    if (changeValue.length > MAXIMUM_NUMBER_OF_EVENT_SECTION) {
      message.error(`기획전 섹션은 최대 ${MAXIMUM_NUMBER_OF_EVENT_SECTION}개 까지만 등록이 가능합니다.`);
      return;
    }

    onChange(changeValue);
  };

  const deleteSection = async (id: number) => {
    if (!onChange) return;

    if (value.length === MINIMUM_NUMBER_OF_EVENT_SECTION) {
      message.error(`기획전 섹션은 최소 ${MINIMUM_NUMBER_OF_EVENT_SECTION}개 이상 등록을 해야합니다.`);
      return;
    }

    const confirmResult = await asyncSimpleModal({
      type: 'confirm',
      title: '기획전 섹션 삭제',
      message: '기획전 섹션을 삭제합니다.\n 계속 진행하시겠습니까?',
    });

    if (!confirmResult) return;

    const updatedSelectEventSectionList = value.filter((item) => item.id !== id);
    onChange(updatedSelectEventSectionList);
    if (id === eventSectionTabKey) {
      setEventSectionTabKey(updatedSelectEventSectionList[0].id);
    }
  };

  const changeSection = (newList: SectionTab[]) => {
    if (!onChange) return;
    onChange([...newList]);
  };

  const handleChangeTab = (eventSectionTabKey: number) => {
    if (!value) return;
    setEventSectionTabKey(value.find((item) => item.id === eventSectionTabKey)?.id ?? null);
  };
  return (
    <>
      <Element name="section-list">
        <Button color="light" onClick={addSection} text="+기획전 섹션 추가" />

        <div className="help-text">드래그 앤 드랍으로 기획전 섹션의 순서를 변경할 수 있습니다.</div>

        <Tabs
          className="event-section-item-tabs"
          type="editable-card"
          hideAdd
          canDrag={true}
          activeKey={eventSectionTabKey}
          onDelete={deleteSection}
          onChange={handleChangeTab}
          onDragEnd={changeSection}
          dataList={value}
          list={contentList}
        />
      </Element>
    </>
  );
});
TabList.displayName = 'TabList';

const INITIAL_VALUE = [
  {
    id: 0,
    name: `section-item-0`,
    type: EVENT_SECTION_TYPE_V2.IMAGE,
    isSectionMobility: false,
  },
];
interface ITabListFormProps extends FormComponentProps {
  initialValue: SectionTab[];
  contentList: SectionContent[];
  eventSectionTabKey: number | null;
  setEventSectionTabKey: React.Dispatch<React.SetStateAction<number | null>>;
}
const TabListForm = forwardRef<FormComponentProps, ITabListFormProps>((props, ref) => {
  const { form, initialValue, contentList, eventSectionTabKey, setEventSectionTabKey } = props;
  const { getFieldDecorator } = form;

  useImperativeHandle(ref, () => ({
    form,
  }));

  return (
    <div className="event-section-list">
      <Form.Item>
        {getFieldDecorator('sectionTabList', {
          initialValue: initialValue.length > 0 ? initialValue : INITIAL_VALUE,
        })(
          <TabList
            contentList={contentList}
            eventSectionTabKey={eventSectionTabKey}
            setEventSectionTabKey={setEventSectionTabKey}
          />,
        )}
      </Form.Item>
    </div>
  );
});
TabListForm.displayName = 'TabListForm';

export default TabListForm;
